import React, { forwardRef } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { compose } from 'redux';

// GLOBAL VARIABLES
import { PLACEHOLDERS, SELECT } from 'defaults.js';

// LOCAL HELPERS
import { handleSelect } from './helpers/handleSelect';
import { defaultOption } from './helpers/defaultOption';
import { selectedOptions } from './helpers/selectedOptions';
import { specialOptions } from './helpers/specialOptions';
import { validateOptions } from './helpers/validateOptions';
import { formikField } from '../../helpers/formik/formikField';
import { formCollapse } from '../../helpers/layout/formCollapse';
import { formGroup } from '../../helpers/layout/formGroup';
import { inputAppend } from '../../helpers/layout/inputAppend';
import { inputGroup } from '../../helpers/layout/inputGroup';
import { inputLabel } from '../../helpers/layout/inputLabel';
import { inputText } from '../../helpers/layout/inputText';
import { inputFeedback } from '../../helpers/layout/inputFeedback';
import { useHidden } from '../../helpers/layout/useHidden';
import { lookupOptions } from '../../helpers/options/lookupOptions';
import { optionsArray } from '../../helpers/options/optionsArray';
import { inputFocus } from '../../helpers/state/inputFocus';
import { inputState } from '../../helpers/state/inputState';
import { inputValidation } from '../../helpers/state/inputValidation';
import { handleAsync } from './helpers/handleAsync';

// REACT-SELECT Components
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';

// LOCAL Components
import { COMPONENTS } from './components/Components.js';
import CustomInput from './components/CustomInput';
import Plaintext from './components/Plaintext';

// LOCALFUNCTIONS
import { filterSelectOptions, formatCreateLabel, noOptionsMessage } from './functions.js';

// STYLES
import styles from './select.module.scss';

// MAIN COMPONENT
const Select = compose(
  inputValidation,
  formikField,
  inputState,
  useHidden,
  formCollapse,
  formGroup,
  inputLabel,
  inputFeedback,
  inputText,
  inputFocus,
  inputAppend,
  inputGroup,
  lookupOptions,
  optionsArray,
  specialOptions,
  handleSelect,
  defaultOption,
  validateOptions,
  selectedOptions,
  handleAsync,
  forwardRef
)(({
  className,
  name,
  value,
  icon,
  options,
  multiple,
  disabled,
  plaintext,
  size,
  append,
  prepend,
  selectedIcon,
  allowCustom = SELECT.allowCustom,
  allowFiltering = SELECT.allowFiltering,
  placeholder = allowCustom ? options.length ? PLACEHOLDERS.customSelect : PLACEHOLDERS.input : PLACEHOLDERS.select,
  useAll,
  useNone,
  onFocus,
  onBlur,
  onChange,
  selectProps = {},
  selectedOptions,
  form: formikBag = {},
  loadOptions,
  // REACT-SELECT OPTIONS
  Component = !!loadOptions ? AsyncSelect : allowCustom ? CreatableSelect : ReactSelect,
  components,
  menuIsOpen,
  menuPlacement = 'auto',
  innerRef,
  openMenuOnFocus = options?.length,
  // REST
  ...props
}, ref) => !!plaintext ? (
    <Plaintext
      selectedOptions={selectedOptions}
      ref={ref}
      {...props}
    />
  ) : (
    <Component
      className={clsx(
        'select',
        allowCustom && 'select-custom-input',
        styles.select,
        size && [`select-${size}`, styles[size]],
        props.fullWidth && styles.fullWidth,
        className
      )}
      placeholder={placeholder}
      name={name}
      value={selectedOptions}
      options={options}
      allowCustom={allowCustom}
      allowFiltering={allowFiltering}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={onChange}
      size={size}
      append={append}
      prepend={prepend}
      formikBag={formikBag}
      ref={ref}
      // START REACT-SELECT OPTIONS
      autosize={true}
      blurInputOnSelect={!multiple && !allowCustom}
      closeMenuOnSelect={!multiple}
      components={Object.assign({}, COMPONENTS, {
        Input: allowCustom && !multiple ? CustomInput : COMPONENTS.Input
      }, components)}
      filterOption={filterSelectOptions}
      formatCreateLabel={formatCreateLabel}
      hideSelectedOptions={false}
      isClearable
      isDisabled={disabled}
      isMulti={multiple}
      menuPlacement={menuPlacement}
      openMenuOnClick={options?.length}
      openMenuOnFocus={openMenuOnFocus}
      noOptionsMessage={noOptionsMessage}
      innerRef={innerRef}
      menuIsOpen={menuIsOpen}
      cacheOptions={!!loadOptions}
      loadOptions={loadOptions}
      // END REACT-SELECT OPTIONS
      {...selectProps}
    />
  )
)

// EXPORT
export default Select;
